import React from 'react';

import cx from 'classnames';
import { Button } from 'components';
import { DayPicker, Matcher } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import {
  getToday,
  fromLocalDate,
  toLocalDate,
} from 'lane-shared/domains/visitorManagement/helpers';
import { DateRangeType } from 'lane-shared/types/baseTypes/DateRangeType';

import { dateRangesToMatcher, formatters, weekends } from '../helpers/helpers';
import { classNames } from './CalendarClassNames';
import CalendarDay from './CalendarDay';
import CalendarHeadAdvanced from './CalendarHeadAdvanced';
import 'react-day-picker/dist/style.css';

import calendarStyles from './Calendar.scss';

type Props = {
  className?: string;
  style?: React.CSSProperties;
  loading?: boolean;
  disabled?: boolean;
  selected?: Date[];
  onChange: (dates: Date[] | undefined) => void;
  onSubmit?: (() => void) | null;
  onFocusChange?: (date: Date) => void;
  maxDate?: Date;
  minDate?: Date;
  unavailableDateRanges?: DateRangeType[];
  weekdayOnly?: boolean;
  placeholder?: string;
  submitLabel?: string;
  testId?: string;
  timeZone?: string;
};

export default function MultiDateCalendar({
  className,
  style,
  loading,
  disabled,
  onChange,
  selected,
  onSubmit,
  onFocusChange,
  maxDate,
  minDate,
  unavailableDateRanges,
  weekdayOnly,
  submitLabel,
  testId,
  timeZone,
}: Props) {
  const { t } = useTranslation();

  const disabledDates = unavailableDateRanges
    ? dateRangesToMatcher(unavailableDateRanges)
    : [];

  if (weekdayOnly) {
    disabledDates.push(weekends);
  }

  const today = getToday(timeZone);

  function handleSelect(dates?: Date[]) {
    if (dates) {
      const parsedDates = dates.map(date => fromLocalDate(date, timeZone)!);

      onChange(parsedDates);
    }
  }

  const localSelected = selected?.map(date => toLocalDate(date, timeZone)!);

  const buttonLabel =
    selected && selected.length > 1
      ? 'web.content.feature.visitorManagement.form.setDates'
      : 'web.content.feature.visitorManagement.form.setDate';

  return (
    <div
      data-test={testId}
      className={cx(calendarStyles.datePicker, className)}
      style={style}
    >
      <DayPicker
        className={disabled ? calendarStyles.disabled : ''}
        classNames={classNames}
        mode="multiple"
        components={{
          Caption: CalendarHeadAdvanced,
          Day: CalendarDay,
        }}
        disabled={disabledDates}
        fromDate={toLocalDate(minDate, timeZone)}
        toDate={toLocalDate(maxDate, timeZone)}
        defaultMonth={localSelected ? localSelected[0] : undefined}
        selected={localSelected}
        modifiers={{ selected: localSelected as Matcher }}
        modifiersClassNames={{ selected: calendarStyles.selected }}
        onSelect={dates => handleSelect(dates)}
        onDayFocus={onFocusChange}
        formatters={formatters}
        today={today}
      />
      {onSubmit && (
        <div
          className={cx(calendarStyles.calendarSubmit, calendarStyles.single)}
        >
          <Button
            className={calendarStyles.button}
            testId="submitDates"
            loading={loading}
            disabled={disabled || !selected || selected.length === 0}
            onClick={onSubmit}
            variant="contained"
          >
            {!submitLabel ? t(buttonLabel) : submitLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
